import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR, { mutate as globalMutate } from 'swr';
import { z } from 'zod';
import { careContext, type DiscoverResponse, type LinkedCareContextSummary } from '@/api/clinical';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { Pagination } from '@/components/Pagination';
import { StatusPill } from '@/components/StatusPill';
import { Wizard, type WizardStep, type WizardStepController } from '@/components/Wizard';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';

/**
 * List of linked care contexts + a three-step linking wizard:
 *   1. Discover patient by ABHA address
 *   2. Pick the care contexts to link
 *   3. Confirm with OTP (when required) or auto-finalise
 */

const PAGE_SIZE = 25;

export function CareContextPage() {
  const [page, setPage] = useState(0);
  const [linking, setLinking] = useState(false);
  const [picked, setPicked] = useState<LinkedCareContextSummary | null>(null);

  const swrKey = ['care-contexts', page] as const;
  const { data, error, isLoading } = useSWR(
    swrKey,
    () => careContext.list({ page, size: PAGE_SIZE }),
  );
  const refresh = () => globalMutate((k) => Array.isArray(k) && k[0] === 'care-contexts');

  return (
    <PageShell
      kicker="TENANT · CARE CONTEXT"
      title="Care Context"
      subtitle="Discover an ABHA address, link selected care contexts, and confirm with the patient. The link is what later flows can pull data from."
      width="wide"
      actions={
        <Stack direction="row" spacing={1}>
          <IconButton onClick={refresh} aria-label="refresh"><RefreshIcon fontSize="small" /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setLinking(true)}>
            Link new
          </Button>
        </Stack>
      }
    >
      <DataTable<LinkedCareContextSummary>
        rows={data?.content}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: AccountTreeIcon,
          title: 'No linked care contexts yet.',
          description: 'Link a patient to make their care contexts pullable in later flows.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setLinking(true)}>Link new</Button>,
        }}
        onRowClick={(r) => setPicked(r)}
        columns={[
          {
            header: 'Patient',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.abhaAddress}</Typography>
                {r.abhaNumber && <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{redactMiddle(r.abhaNumber, 4, 4)}</Typography>}
              </Stack>
            ),
          },
          {
            header: 'Contexts',
            align: 'right',
            cell: (r) => (r.careContexts?.length ?? 0).toLocaleString(),
            width: 110,
          },
          {
            header: 'Status',
            cell: (r) => {
              const tone = r.status === 'LINKED' ? 'success' : r.status === 'PENDING' ? 'info' : 'mono';
              return <StatusPill tone={tone}>{r.status}</StatusPill>;
            },
            width: 130,
          },
          {
            header: 'Updated',
            cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedAt)}</Typography>,
            width: 140,
          },
        ]}
      />
      <Pagination page={data} onChange={setPage} />

      <LinkWizardDialog open={linking} onClose={() => setLinking(false)} onLinked={refresh} />
      <DetailDialog ctx={picked} onClose={() => setPicked(null)} />
    </PageShell>
  );
}

// -------------------- Wizard --------------------

function LinkWizardDialog({ open, onClose, onLinked }: { open: boolean; onClose: () => void; onLinked: () => void }) {
  const theme = useTheme();
  const toast = useToast();

  const [discovery, setDiscovery] = useState<DiscoverResponse | null>(null);
  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  const [requiresOtp, setRequiresOtp] = useState(false);

  useEffect(() => {
    if (open) {
      setDiscovery(null);
      setSelectedRefs([]);
      setRequiresOtp(false);
    }
  }, [open]);

  const steps: WizardStep[] = [
    {
      id: 'discover',
      title: 'Discover',
      hint: 'find by ABHA address',
      content: (ctrl) => (
        <DiscoverStep ctrl={ctrl} onResult={(d) => { setDiscovery(d); setSelectedRefs([]); }} />
      ),
    },
    {
      id: 'select',
      title: 'Select',
      hint: 'pick contexts',
      content: (ctrl) => (
        <SelectStep
          ctrl={ctrl}
          discovery={discovery}
          selectedRefs={selectedRefs}
          onSelectedRefs={setSelectedRefs}
          onLinkInit={(r) => setRequiresOtp(r.requiresOtp)}
        />
      ),
    },
    {
      id: 'confirm',
      title: 'Confirm',
      hint: requiresOtp ? 'OTP required' : 'auto',
      content: (ctrl) => (
        <ConfirmStep
          ctrl={ctrl}
          transactionId={discovery?.transactionId ?? null}
          requiresOtp={requiresOtp}
        />
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600, letterSpacing: -0.2 }}>
        Link care context
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Wizard
          steps={steps}
          onComplete={() => {
            toast.success('Care context linked');
            onLinked();
            onClose();
          }}
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

const discoverSchema = z.object({
  abhaAddress: z.string().min(3, 'ABHA address is required'),
  patientName: z.string().optional(),
  yearOfBirth: z.union([z.string(), z.number()]).optional(),
  gender: z.enum(['M', 'F', 'O']).optional(),
});
type DiscoverForm = z.infer<typeof discoverSchema>;

function DiscoverStep({ ctrl, onResult }: { ctrl: WizardStepController; onResult: (d: DiscoverResponse) => void }) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<DiscoverForm>({ defaultValues: { abhaAddress: '' } });
  const abhaAddress = watch('abhaAddress');

  useEffect(() => {
    ctrl.setNextLabel('Discover');
    ctrl.setNextEnabled(!!abhaAddress && abhaAddress.length >= 3);
  }, [abhaAddress, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const v = getValues();
      const parsed = discoverSchema.safeParse(v);
      if (!parsed.success) return false;
      try {
        const r = await careContext.discover({
          abhaAddress: parsed.data.abhaAddress,
          patientName: parsed.data.patientName,
          yearOfBirth: parsed.data.yearOfBirth ? Number(parsed.data.yearOfBirth) : undefined,
          gender: parsed.data.gender,
        });
        toast.success('Patient found', `${r.patient.display} · ${r.patient.careContexts?.length ?? 0} contexts`);
        onResult(r);
        return true;
      } catch (e) {
        toast.error('Discovery failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, onResult, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Find the patient</Typography>
        <Typography variant="body2" color="text.secondary">
          ABHA address is enough; the optional fields disambiguate when the address belongs to multiple registered identities.
        </Typography>
      </Stack>
      <TextField autoFocus label="ABHA address" placeholder="patient@abdm" fullWidth {...register('abhaAddress')} error={!!errors.abhaAddress} helperText={errors.abhaAddress?.message} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
        <TextField label="Name" {...register('patientName')} />
        <TextField label="Year of birth" type="number" {...register('yearOfBirth')} />
        <TextField label="Gender" select SelectProps={{ native: true }} defaultValue="" {...register('gender')}>
          <option value=""></option>
          <option value="M">Male</option>
          <option value="F">Female</option>
          <option value="O">Other</option>
        </TextField>
      </Box>
    </Stack>
  );
}

function SelectStep({
  ctrl,
  discovery,
  selectedRefs,
  onSelectedRefs,
  onLinkInit,
}: {
  ctrl: WizardStepController;
  discovery: DiscoverResponse | null;
  selectedRefs: string[];
  onSelectedRefs: (refs: string[]) => void;
  onLinkInit: (r: { requiresOtp: boolean }) => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const contexts = discovery?.patient.careContexts ?? [];

  useEffect(() => {
    ctrl.setNextLabel('Send link request');
    ctrl.setNextEnabled(selectedRefs.length > 0);
  }, [selectedRefs.length, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      if (!discovery) return false;
      try {
        const r = await careContext.linkInit({
          transactionId: discovery.transactionId,
          referenceNumber: discovery.patient.referenceNumber,
          careContextReferences: selectedRefs,
        });
        toast.success(r.requiresOtp ? 'OTP sent to patient' : 'Link initiated');
        onLinkInit(r);
        return true;
      } catch (e) {
        toast.error('Link request failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [discovery, selectedRefs, onLinkInit, toast, ctrl]);

  if (!discovery) {
    return <Typography variant="body2" color="text.secondary">No discovery result — go back.</Typography>;
  }

  const toggle = (ref: string) =>
    onSelectedRefs(selectedRefs.includes(ref) ? selectedRefs.filter((r) => r !== ref) : [...selectedRefs, ref]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>{discovery.patient.display}</Typography>
        <Typography variant="body2" color="text.secondary">
          Tick the care contexts you want to link. The patient confirms next.
        </Typography>
      </Stack>
      {contexts.length === 0 ? (
        <Typography variant="body2" color="text.tertiary">No care contexts available for this patient.</Typography>
      ) : (
        <Stack spacing={0} sx={{ border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.sm + 'px' }}>
          {contexts.map((c) => {
            const selected = selectedRefs.includes(c.referenceNumber);
            return (
              <Stack
                key={c.referenceNumber}
                direction="row"
                spacing={1.5}
                alignItems="center"
                onClick={() => toggle(c.referenceNumber)}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.border.subtle}`,
                  cursor: 'pointer',
                  transition: `background-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
                  '&:hover': { bgcolor: theme.palette.surface.sunken },
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Checkbox checked={selected} size="small" sx={{ p: 0.5 }} />
                <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{c.display}</Typography>
                  <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {c.referenceNumber}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

const otpSchema = z.object({ otp: z.string().regex(/^\d{4,6}$/, 'Enter the OTP') });
type OtpForm = z.infer<typeof otpSchema>;

function ConfirmStep({
  ctrl,
  transactionId,
  requiresOtp,
}: {
  ctrl: WizardStepController;
  transactionId: string | null;
  requiresOtp: boolean;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<OtpForm>({ defaultValues: { otp: '' } });
  const otp = watch('otp');

  useEffect(() => {
    ctrl.setNextLabel(requiresOtp ? 'Confirm' : 'Finalise');
    ctrl.setNextEnabled(!requiresOtp || /^\d{4,6}$/.test(otp));
  }, [otp, requiresOtp, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      if (!transactionId) return false;
      try {
        if (requiresOtp) {
          const parsed = otpSchema.safeParse(getValues());
          if (!parsed.success) return false;
          await careContext.linkConfirm({ transactionId, otp: parsed.data.otp });
        } else {
          await careContext.linkConfirm({ transactionId });
        }
        return true;
      } catch (e) {
        toast.error('Link confirmation failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [transactionId, requiresOtp, getValues, toast, ctrl]);

  if (!requiresOtp) {
    return (
      <Stack spacing={2}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Auto-confirmed flow</Typography>
        <Typography variant="body2" color="text.secondary">
          ABDM didn't require an OTP for this link request. Click Finalise to record the link.
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>OTP from the patient</Typography>
        <Typography variant="body2" color="text.secondary">
          The patient received a code on their registered mobile. Enter it to confirm the link.
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="OTP"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { fontFamily: 'JetBrains Mono, monospace', letterSpacing: 4, fontSize: 20 } }}
        {...register('otp')}
        error={!!errors.otp}
        helperText={errors.otp?.message ?? ' '}
      />
    </Stack>
  );
}

// -------------------- Detail dialog --------------------

function DetailDialog({ ctx, onClose }: { ctx: LinkedCareContextSummary | null; onClose: () => void }) {
  const theme = useTheme();
  return (
    <Dialog open={!!ctx} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {ctx?.abhaAddress ?? 'Care context'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {ctx && (
          <>
            <FieldRow label="ABHA address" value={ctx.abhaAddress} mono />
            <FieldRow label="ABHA number" value={ctx.abhaNumber ?? '—'} mono />
            <FieldRow label="Status" value={<StatusPill tone={ctx.status === 'LINKED' ? 'success' : 'info'}>{ctx.status}</StatusPill>} />
            <FieldRow label="Updated" value={ctx.updatedAt ? relativeTime(ctx.updatedAt) : '—'} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 1 }}>
                Care contexts ({ctx.careContexts.length})
              </Typography>
              <Stack spacing={0} sx={{ border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.sm + 'px' }}>
                {ctx.careContexts.map((c) => (
                  <Stack
                    key={c.referenceNumber}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderBottom: `1px solid ${theme.palette.border.subtle}`,
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>{c.display}</Typography>
                    <Typography variant="caption" color="text.tertiary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {c.referenceNumber}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
